import React from "react";
import { Button } from "@/components/ui/Button/Button";
import { Typography } from "@/components/ui/Typography/Typography";
import {
  TRole,
  TEnhancedRole,
} from "@/api/role-management/role-management.types";
import {
  Users,
  Shield,
  Calendar,
  Edit,
  Eye,
  Trash2,
  ArrowRight,
  Activity,
  Clock,
  PauseCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatLocaleDate } from "@/utils/commonUtils";
import { cn } from "@/utils";
import CommonCardUI from "@/components/ui/Card/CommonCardUI";

type TRoleCardProps = {
  role: TRole | TEnhancedRole;
  isSelected: boolean;
  onSelect: (role: TRole | TEnhancedRole) => void;
  onEdit: (role: TRole | TEnhancedRole) => void;
  onView: (role: TRole | TEnhancedRole) => void;
  onDelete: (role: TRole | TEnhancedRole) => void;
};

export const RoleCard: React.FC<TRoleCardProps> = ({
  role,
  isSelected,
  onSelect,
  onEdit,
  onView,
  onDelete,
}) => {
  // Handle both TRole and TEnhancedRole types
  const { t } = useTranslation("roleManagementPage");
  const permissionCount =
    "permission_count" in role ? (role.permission_count ?? 0) : 0;
  const userCount =
    "user_count" in role
      ? role.user_count || 0
      : (role.assignment_statistics?.total_assignments ?? 0);
  const activeAssignments = role.assignment_statistics?.active_assignments ?? 0;
  const recentAssignments =
    role.assignment_statistics &&
    "recent_assignments" in role.assignment_statistics
      ? role.assignment_statistics.recent_assignments || 0
      : 0;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return formatLocaleDate(date);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger selection if clicking on action buttons
    if ((e.target as HTMLElement).closest("button")) {
      return;
    }
    onSelect(role);
  };

  return (
    <CommonCardUI
      className={cn(
        "w-full h-full flex flex-col justify-between bg-background relative cursor-pointer transition hover:-translate-y-1 hover:shadow-xl overflow-hidden",
        isSelected
          ? "border-primary shadow-md ring-2 ring-primary/20"
          : "border-border",
      )}
      classNameContent="p-3"
      onClick={handleCardClick}
    >
      {/* Card Header */}
      <div className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Typography
                variant="h6"
                affects={"semiBold"}
                className="truncate"
              >
                {role.name}
              </Typography>
            </div>
            {/* <Typography variant="small" className="text-muted-foreground mb-2">
              {role.slug}
            </Typography> */}
            <Typography
              variant="small"
              className="text-muted-foreground line-clamp-2 min-h-[32px]"
              title={role.description}
            >
              {role.description || t("ROLES.ROLE_CARD.NO_DESCRIPTION_PROVIDED")}
            </Typography>
          </div>

          {/* Status indicator */}
          <div className="flex-shrink-0 ml-3">
            <span
              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full items-center gap-1 ${role.is_active ? "bg-green/11 text-green" : "bg-gray/11 text-gray"}`}
            >
              {role.is_active ? (
                <span className="status-circle bg-green"></span>
              ) : (
                <PauseCircle className="w-3 h-3" />
              )}
              <Typography variant={"article"} className="text-xs">
                {role.is_active ? t("GLOBAL.ACTIVE") : t("GLOBAL.INACTIVE")}
              </Typography>
            </span>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="pb-2">
        <div className="grid grid-cols-2 gap-4">
          {/* Permissions */}
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <Typography variant="small" className="text-muted-foreground">
                {t("ROLES.ROLE_CARD.PERMISSIONS")}
              </Typography>
              <Typography
                variant="p"
                className="font-semibold text-card-foreground"
              >
                {permissionCount.toLocaleString()}
              </Typography>
            </div>
          </div>

          {/* Users */}
          <div className="flex items-center gap-2 justify-end">
            <div className="flex-shrink-0 w-8 h-8 bg-green/10 rounded-lg flex items-center justify-center">
              <Users className="w-4 h-4 text-green" />
            </div>
            <div className="min-w-0">
              <Typography variant="small" className="text-muted-foreground">
                {t("ROLES.ROLE_CARD.ASSIGNMENTS")}
              </Typography>
              <Typography
                variant="p"
                className="font-semibold text-card-foreground"
              >
                {userCount.toLocaleString()}
              </Typography>
            </div>
          </div>
        </div>

        {/* Additional stats if available */}
        {(activeAssignments > 0 || recentAssignments > 0) && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              {activeAssignments > 0 && (
                <div className="flex items-center gap-1">
                  <Activity size={14} />
                  <span>
                    {activeAssignments} {t("GLOBAL.ACTIVE")}
                  </span>
                </div>
              )}
              {recentAssignments > 0 && (
                <div className="flex items-center gap-1">
                  <Clock size={14} />
                  <span>
                    {recentAssignments} {t("ROLES.ROLE_CARD.RECENT")}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer with dates and actions */}
      <div className="py-2 rounded-b-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Calendar size={14} />
            <span>
              {t("ROLES.ROLE_CARD.UPDATED")}{" "}
              {formatDate(
                "last_modified" in role ? role.last_modified : role.updated_on,
              )}
            </span>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1">
            <Button
              variant="action"
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                onView(role);
              }}
              className="p-1 rounded"
              title="View details"
              id="view-role-button"
            >
              <Eye size={14} />
            </Button>
            <Button
              variant="action"
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(role);
              }}
              className="p-2 rounded"
              title="Edit role"
              id="edit-role-button"
            >
              <Edit size={14} />
            </Button>
            <Button
              variant="action"
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(role);
              }}
              className="p-2 rounded"
              title="Delete role"
              id="delete-role-button"
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </div>
      </div>

      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-4 right-4 w-6 h-6 rounded-full flex items-center justify-center">
          <ArrowRight size={14} className="text-primary-foreground" />
        </div>
      )}

      {/* Hover overlay for selection */}
      <div className="absolute inset-0 rounded-lg bg-opacity-0 hover:bg-opacity-5 transition-all duration-200 pointer-events-none" />
    </CommonCardUI>
  );
};
